import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineDotsVertical,
  HiOutlinePencil,
  HiOutlineStar,
  HiOutlineTrash,
  HiOutlineX,
  HiStar,
} from "react-icons/hi";
import { FiSave } from "react-icons/fi";
import { classNames } from "Utils/utils";
import { formatChatTimestamp } from "../utils/vertex360CopilotChatUtils";
import {
  VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS,
  VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS,
  displayConversationTitle,
} from "../constants/vertex360CopilotWorkspaceConstants";

const MENU_WIDTH_PX = 176;
const MENU_ITEM_HEIGHT_PX = 36;
const MENU_PADDING_PX = 8;
const MENU_GAP_PX = 4;

const Vertex360CopilotConversationRow = ({
  conversation,
  isActive = false,
  showCheckbox = false,
  isSelected = false,
  onOpen,
  onStar,
  onUnstar,
  onRename,
  onDelete,
  onToggleSelect,
}) => {
  const visibleTitle = displayConversationTitle(
    conversation.title,
    (conversation.messages || []).find((item) => item?.role === "user")?.content
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [renameValue, setRenameValue] = useState(visibleTitle);
  const menuButtonRef = useRef(null);
  const renameInputRef = useRef(null);

  const menuItemCount = 3;
  const menuHeight = menuItemCount * MENU_ITEM_HEIGHT_PX + MENU_PADDING_PX;

  useEffect(() => {
    if (!isRenaming) {
      setRenameValue(visibleTitle);
    }
  }, [visibleTitle, isRenaming]);

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  const updateMenuPosition = useCallback(() => {
    const button = menuButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight + MENU_GAP_PX && spaceAbove > menuHeight;

    setMenuPosition({
      top: openUpward
        ? rect.top - menuHeight - MENU_GAP_PX
        : rect.bottom + MENU_GAP_PX,
      left: Math.max(8, rect.right - MENU_WIDTH_PX),
    });
  }, [menuHeight]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    updateMenuPosition();

    const handleReposition = () => updateMenuPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isMenuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        menuButtonRef.current?.contains(event.target) ||
        event.target.closest("[data-copilot-conversation-menu]")
      ) {
        return;
      }
      setIsMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const cancelRename = useCallback(() => {
    setRenameValue(visibleTitle);
    setIsRenaming(false);
  }, [visibleTitle]);

  const commitRename = useCallback(() => {
    const next = renameValue.trim();
    if (!next) return;
    if (next !== conversation.title) {
      onRename?.(conversation.id, next);
    }
    setIsRenaming(false);
  }, [conversation.id, conversation.title, onRename, renameValue]);

  const canSaveRename =
    Boolean(renameValue.trim()) && renameValue.trim() !== conversation.title;

  const handleRowClick = () => {
    if (isRenaming) return;
    onOpen?.(conversation.id);
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    onToggleSelect?.(conversation.id);
  };

  const handleMenuToggle = (event) => {
    event.stopPropagation();
    setIsMenuOpen((open) => !open);
  };

  const handleMenuAction = (event, action) => {
    event.stopPropagation();
    setIsMenuOpen(false);

    if (action === "unstar") onUnstar?.(conversation.id);
    if (action === "star") onStar?.(conversation.id);
    if (action === "rename") setIsRenaming(true);
    if (action === "delete") onDelete?.(conversation.id);
  };

  const menuItems = [
    conversation.starred
      ? {
          id: "unstar",
          label: "Unstar",
          icon: HiStar,
          iconClassName: "text-amber-500",
          className: "text-slate-700",
        }
      : {
          id: "star",
          label: "Star",
          icon: HiOutlineStar,
          className: "text-slate-700",
        },
    {
      id: "rename",
      label: "Rename",
      icon: HiOutlinePencil,
      className: "text-slate-700",
    },
    {
      id: "delete",
      label: "Delete",
      icon: HiOutlineTrash,
      className: "text-red-600",
    },
  ];

  return (
    <div
      className={classNames(
        "group relative flex transition-colors",
        showCheckbox
          ? "items-start gap-3 px-4 py-3 pr-3"
          : "items-center gap-1.5 rounded-lg px-1 py-1.5",
        !showCheckbox && "rounded-lg",
        !showCheckbox && VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS,
        !showCheckbox && isActive && VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS,
        !showCheckbox &&
          !isActive &&
          "text-slate-700 hover:bg-[#ECF0FD]/80 hover:text-[#293F6A]",
        showCheckbox &&
          (isActive
            ? "bg-[#ECF0FD] text-[#293F6A]"
            : "text-slate-700 hover:bg-[#ECF0FD]/80 hover:text-[#293F6A]"),
        isMenuOpen && "z-10"
      )}
    >
      {showCheckbox ? (
        <label
          className="flex shrink-0 cursor-pointer items-center justify-center pt-0.5"
          onClick={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxClick}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-[#4B6CB7] focus:ring-[#4B6CB7]"
            aria-label={`Select ${visibleTitle}`}
          />
        </label>
      ) : null}

      {isRenaming ? (
        <div
          className={classNames(
            "min-w-0 flex-1 overflow-hidden",
            !showCheckbox &&
              "grid grid-cols-[0.875rem_minmax(0,1fr)] items-center gap-1.5"
          )}
        >
          {!showCheckbox ? (
            conversation.starred ? (
              <HiStar className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )
          ) : null}

          <div
            className="flex w-full min-w-0 items-center gap-0.5 rounded-md border border-[#C7D7F5] bg-white py-0.5 pl-2.5 pr-1 shadow-sm shadow-[#4B6CB7]/8 ring-1 ring-[#4B6CB7]/10 transition-all focus-within:border-[#4B6CB7] focus-within:ring-2 focus-within:ring-[#4B6CB7]/20"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (canSaveRename) commitRename();
                  else if (renameValue.trim()) setIsRenaming(false);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancelRename();
                }
              }}
              className="min-w-0 flex-1 border-0 bg-transparent py-1 text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0"
              aria-label="Rename conversation"
            />
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                commitRename();
              }}
              disabled={!canSaveRename}
              className="ml-1 box-border inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-solid border-green-200 bg-green-100 text-green-600 transition-colors duration-150 hover:border-green-500 hover:bg-green-200 hover:text-green-700 active:border-green-600 active:bg-green-300 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Save rename"
            >
              <FiSave className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                cancelRename();
              }}
              className="box-border inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-solid border-red-300 bg-red-100 text-red-500 transition-colors duration-150 hover:border-red-400 hover:bg-red-200 hover:text-red-600 active:border-red-500 active:bg-red-300"
              aria-label="Cancel rename"
            >
              <HiOutlineX className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleRowClick}
          className={classNames(
            "min-w-0 flex-1 overflow-hidden text-left",
            !showCheckbox &&
              "grid grid-cols-[0.875rem_minmax(0,1fr)] items-center gap-1.5"
          )}
        >
          {!showCheckbox ? (
            conversation.starred ? (
              <HiStar className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            ) : (
              <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )
          ) : null}

          <span className="block min-w-0 overflow-hidden">
            <span className="copilot-sidebar-row-title block text-sm font-medium leading-5">
              {visibleTitle}
            </span>
            {showCheckbox ? (
              <span className="mt-0.5 block truncate text-[11px] text-slate-400">
                {formatChatTimestamp(conversation.updatedAt)}
              </span>
            ) : null}
          </span>
        </button>
      )}

      {!isRenaming ? (
        <>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={handleMenuToggle}
            className={classNames(
              "inline-flex h-7 w-6 shrink-0 items-center justify-center rounded-md text-[#4B6CB7] transition-all duration-150",
              showCheckbox && "mt-0.5 self-start",
              "hover:bg-white/90 hover:text-[#293F6A] focus:outline-none focus:ring-2 focus:ring-[#4B6CB7]/40",
              isActive || isMenuOpen
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 focus:opacity-100"
            )}
            aria-label={`Actions for ${visibleTitle}`}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
          >
            <HiOutlineDotsVertical className="h-4 w-4" />
          </button>

          {typeof document !== "undefined" && isMenuOpen
            ? createPortal(
                <div
                  data-copilot-conversation-menu
                  role="menu"
                  className="fixed z-[10050] w-44 origin-top-right rounded-lg border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
                  style={{
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                  }}
                >
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        onClick={(event) => handleMenuAction(event, item.id)}
                        className={classNames(
                          "flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-slate-50",
                          item.className
                        )}
                      >
                        <Icon
                          className={classNames(
                            "h-4 w-4 shrink-0",
                            item.iconClassName
                          )}
                        />
                        {item.label}
                      </button>
                    );
                  })}
                </div>,
                document.body
              )
            : null}
        </>
      ) : null}
    </div>
  );
};

export default Vertex360CopilotConversationRow;
